import { ErrorBoundary } from "react-error-boundary";
import { Suspense, useEffect, useState } from "react";
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";

import { api } from "~/data/core/api";
import { socket } from "~/data/core/socket";

import { Button } from "../shared/button";
import { InputSearch } from "../shared/input";
import { UserAvatar } from "../shared/user-avatar";
import { useUser } from "../shared/queries/user-query";

interface ActiveUser {
  id: string;
  name: string;
  chat_status: string;
  avatar_url: string;
}

export function Users() {
  const user = useUser();
  const queryClient = useQueryClient();
  useEffect(
    () =>
      socket.on("chat_status_changed", (newUser) =>
        queryClient.setQueryData<ActiveUser[]>(
          ["users"],
          (currentUsers = []) => {
            if (newUser.user_id === user.id) return currentUsers;

            const users = Array.from(currentUsers);
            const index = currentUsers.findIndex(
              (it) => it.id === newUser.user_id
            );
            const wasOnline = index != -1;
            if (wasOnline && newUser.chat_status === "offline") {
              users.splice(index, 1);
            }
            if (!wasOnline && newUser.chat_status === "online") {
              users.push({ ...newUser, id: newUser.user_id });
            }
            return users;
          }
        )
      ),
    [queryClient, user.id]
  );

  const [search, setSearch] = useState("");

  return (
    <div className="p-10 bg-white h-full">
      <div className="py-2 px-8 mb-5">
        <h2 className="text-3xl font-semibold font-alternative mb-1">
          Colegas
        </h2>
        <p className="text-[#646464] text-sm font-alternative leading-4">
          Lista com todos os usuários online na plataforma que você pode
          convidar para estudar
        </p>
      </div>
      <section>
        <div className="flex items-center justify-between px-8 py-2 mb-5">
          <h3 className="font-alternative text-xl font-semibold">
            Usuários Online
          </h3>

          <InputSearch
            className="ml-auto w-full max-w-[264px]"
            placeholder="Busque por nome"
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <ErrorBoundary
          fallback={
            <p className="py-10 text-center">
              Houve um erro ao obter os usuários online
            </p>
          }
        >
          <Suspense
            fallback={
              <div className="grid gap-5">
                <div className="rounded-[10px] h-[88px] bg-gray-100 animate-pulse" />
                <div className="rounded-[10px] h-[88px] bg-gray-100 animate-pulse" />
                <div className="rounded-[10px] h-[88px] bg-gray-100 animate-pulse" />
              </div>
            }
          >
            <UsersList q={search} />
          </Suspense>
        </ErrorBoundary>
      </section>
    </div>
  );
}

function UsersList({ q = "" }) {
  const { data: users } = useSuspenseQuery({
    queryKey: ["users"],
    queryFn: async () => {
      socket.send({
        command: "message",
        identifier: socket.channels.chats(),
        data: { action: "get_users" },
      });
      return Promise.race([
        new Promise<ActiveUser[]>((resolve) =>
          socket.once("online_users", resolve)
        ),
        new Promise<ActiveUser[]>((resolve) =>
          socket.once("get_users", resolve)
        ),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject("Timeout getting users"), 5000)
        ),
      ]);
    },
    select: (data) =>
      q
        ? data.filter((it) => it.name.toLowerCase().includes(q.toLowerCase()))
        : data,
  });
  const callUserMutation = useMutation({
    mutationKey: ["call_user"],
    mutationFn: async (mateId: string) => {
      const { data } = await api.post(`/api/v1/chat_rooms`, {
        user2_id: mateId,
      });
      const chatRoom = data.data;
      return chatRoom;
    },
  });

  if (!users.length) {
    return (
      <p className="py-10 text-center">
        Nenhum usuário online {q ? "para a sua busca" : "no momento"}
      </p>
    );
  }

  return (
    <ul className="grid gap-5">
      {users.map((it) => (
        <li
          key={it.id}
          className="flex items-center gap-5 px-6 py-4 rounded-[10px] border-l-[6px] border-l-primary-1 bg-[#FBFBFB]"
          style={{ boxShadow: "0px 10px 20px 0px rgba(0, 0, 0, 0.08)" }}
        >
          <UserAvatar size={36} url={it.avatar_url} name={it.name} />
          <span className="flex-1 text-base leading-4 font-alternative">
            {it.name}
          </span>
          <Button
            variant="primary-1"
            className="w-60 h-10"
            disabled={callUserMutation.isPending}
            loading={
              callUserMutation.isPending && callUserMutation.variables === it.id
            }
            onClick={async () => {
              const devices = await navigator.mediaDevices.enumerateDevices();
              const hasAudio = devices.find((it) => it.kind === "audioinput");
              const hasVideo = devices.find((it) => it.kind === "videoinput");

              // Audio input is required, so we early return here
              if (!hasAudio) {
                alert(
                  "É necessário ter um dispositivo de áudio conectado para participar da chamada!"
                );
                return;
              }

              // Video input is recommended but not required
              if (!hasVideo) {
                alert(
                  "É recomendado ter um dispositivo de vídeo conectado para participar da chamada."
                );
              }

              callUserMutation.mutate(it.id);
            }}
          >
            Praticar com colega
          </Button>
        </li>
      ))}
    </ul>
  );
}
